/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Popper } from "@mui/material";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import { areaEnum } from "config/enum";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import HelpIcon from "@mui/icons-material/Info";
import { styled } from "@mui/material/styles";

function DataTableHeadCell({
  isOddWeek,
  length,
  width,
  children,
  sorted,
  align,
  colspan,
  original,
  info,
  accessor,
  rows,
  canSearch,
  foremans,
  userList,
  planningRows,
  setPlanningRows,
  setPlanningUsers,
  planningUsers,
  todayRef,
  firstMonthDayRef,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [displayNone, setDisplayNone] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rowsNumber, setRowsNumber] = useState(0);
  const [doIt, setDoIt] = useState(false);

  // Reference to the dropdown wrapper element
  const dropdownWrapperRef = useRef(null);

  const dispatch = useDispatch();

  // Handle closing the dropdown when clicking away
  const handleClickAway = () => {
    setDropdownOpen(false);
  };

  // Combine users and areas into a single options array
  const combinedOptions = areaEnum &&
    userList &&
    foremans && [
      ...foremans.map((foreman) => ({
        group: "Contremaîtres",
        id: foreman?.id,
        name: foreman?.label,
      })),
      ...areaEnum.map((area) => ({ group: "Zones géographiques", name: area })),
      ...userList.map((user) => ({
        group: "Ressources",
        id: user.user_id,
        name: user.user_name,
        area: user.user_area,
        role: user.user_role,
        foremans: user.user_foremans,
      })),
    ];

  useEffect(() => {
    // Keep the dropdown open by setting the state
    if (planningRows.length !== 0 && planningUsers.length === 0) {
      setRowsNumber(rows && rows);
      setDropdownOpen(false);
    } else if (planningRows.length !== 0 && planningUsers.length !== 0) {
      setRowsNumber(rows && rows);
      setDropdownOpen(true);
    } else if (planningRows.length === 0 && planningUsers.length === 0) {
      setRowsNumber(0);
      setDropdownOpen(false);
    }
  }, [planningRows, planningUsers, rows]);

  useEffect(() => {
    if (planningRows.lenght !== 0 && planningUsers.lenght !== 0) {
      setDisplayNone(true);
    }
    if (planningRows.length !== 0 && planningUsers.lenght === 0) {
      setDisplayNone(true);
    }
    if (planningRows.length === 0 && planningUsers.length === 0) {
      setDisplayNone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsNumber]);

  // This function will be called when an option is selected
  const handleSelect = (event, newValue) => {
    // Prevent default and stop propagation to ensure the dropdown stays open
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const valueToStore = newValue.map((selectedItem) =>
      combinedOptions.find(
        (option) => option.name === selectedItem.name && option.group === selectedItem.group
      )
    );

    dispatch(setPlanningRows(valueToStore));
    setPlanningUsers(valueToStore);

    if (valueToStore.length !== 0) {
      setDoIt(true);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (doIt) setDropdownOpen(true);
    }, 1);
    setDoIt(false);
  }, [doIt]);

  const isPlanning = info.includes("planning");
  const isMonth = info.includes("month");
  const isWeek = info.includes("week");
  const isDay = info.includes("day");
  const isFirst = length === 13 && original.includes("users_placeholder");

  const renderTags = (value, getTagProps) => {
    return (
      <Chip
        sx={{
          color: darkMode ? "white !important" : "inherit !important",
          display: displayNone === false ? "none" : "flex",
        }}
        key={rowsNumber}
        variant="outlined"
        label={`${rowsNumber}`}
      />
    );
  };

  function findFirstDisplayedDayOfMonth() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    // Get the day of the week: 0 (Sunday) to 6 (Saturday)
    const dayOfWeek = firstDayOfMonth.getDay();

    // If it's Saturday (6) or Sunday (0), adjust to next Monday
    if (dayOfWeek === 0) {
      // If Sunday, add 1 day to get to Monday
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 1);
    } else if (dayOfWeek === 6) {
      // If Saturday, add 2 days to get to Monday
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 2);
    }

    // Return the first displayed day as YYYY-MM-DD for comparison
    return firstDayOfMonth.toLocaleDateString("fr-FR").split("T")[0];
  }

  const isRatioEfficiency = typeof children === "string" && children?.includes("Efficacité");

  const isRatioPerformance = typeof children === "string" && children?.includes("Performance");

  const isWait = typeof children === "string" && children?.includes("Délai fabrication appareil");

  const isPeriod = typeof children === "string" && children?.includes("Durée chantier");

  const InfoTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "rgba(97, 97, 97, 0.92)",
      borderRadius: "4px",
      color: "#fff",
      padding: "4px 8px",
      fontSize: "0.802rem",
      maxWidth: "279px",
      margin: "0",
      wordWrap: "break-word",
      fontWeight: "500",
      position: "relative",
      "span.MuiTooltip-arrow": { color: "rgba(97, 97, 97, 0.92)" },
    },
  }));

  return (
    <MDBox
      display={
        children === "" || (typeof children === "object" && length !== 13) ? "none" : "table-cell"
      }
      rowSpan={isFirst ? 3 : 1}
      colSpan={
        info.includes("planning week") &&
        children !== "S1" &&
        children !== "S52" &&
        children !== "S53"
          ? 5
          : colspan
      }
      ref={
        accessor && accessor === new Date().toLocaleDateString("fr-FR")
          ? todayRef
          : accessor && accessor === findFirstDisplayedDayOfMonth()
          ? firstMonthDayRef
          : null
      }
      component="th"
      width={width}
      py={1.5}
      px={isRatioEfficiency || isRatioPerformance ? 0 : 1.5}
      sx={({ palette: { light }, borders: { borderWidth } }) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
        background: isOddWeek ? "#f2f2f2 !important" : "white !important",
        position: "sticky !important",
        top: 0,
      })}
    >
      <MDBox
        {...rest}
        position="relative"
        textAlign={align}
        color={darkMode ? "white" : isPlanning ? "#344767" : "secondary"}
        opacity={isPlanning ? 1 : 0.7}
        sx={({ typography: { size, fontWeightBold } }) => ({
          fontSize: isMonth ? size.md : isDay || isWeek ? "0.875rem" : size.xs,
          fontWeight: fontWeightBold,
          textTransform: "uppercase",
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
        })}
      >
        {typeof children === "object" && length === 13 ? (
          canSearch && (
            <MDBox sx={{ height: "90px" }}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Autocomplete
                  multiple
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name && option.group === value.group
                  }
                  options={combinedOptions}
                  groupBy={(option) => option.group}
                  getOptionLabel={(option) => option.name}
                  value={planningRows}
                  PopperComponent={({ ...props }) => <Popper {...props} ref={dropdownWrapperRef} />}
                  /* renderOption={(props, option) => {
                      return (
                        <MDBox
                          component="li"
                          {...props}
                          style={{
                            color: areaColor[option.name],
                            fontWeight: option.area === undefined && "bold",
                          }}
                        >
                          {option.name}
                        </MDBox>
                      );
                    }} */
                  open={dropdownOpen}
                  onOpen={() => setDropdownOpen(true)}
                  onClose={() => setDropdownOpen(false)}
                  onChange={handleSelect}
                  renderInput={(params) => (
                    <TextField
                      placeholder="Rechercher..."
                      sx={{
                        "& fieldset": {
                          borderRadius: "0px !important",
                          border: "none",
                        },
                        /* "input.MuiOutlinedInput-input": {
                            display: displayNone ? "none" : "block",
                          }, */
                        "& .MuiOutlinedInput-root": {
                          height: "90px !important",
                        },
                        height: "90px !important",
                        borderRadius: "0px !important",
                      }}
                      {...params}
                    />
                  )}
                  renderTags={renderTags}
                />
              </ClickAwayListener>
            </MDBox>
          )
        ) : isRatioPerformance || isRatioEfficiency || isWait || isPeriod ? (
          <>
            {children}
            <InfoTooltip
              placement="top"
              arrow
              title={
                <p
                  style={{
                    margin: "0",
                    fontSize: "0.802rem !important",
                  }}
                >
                  {isRatioEfficiency
                    ? "Efficacité = (Heures réelles / Heures prévues)"
                    : isWait
                    ? "Nombre de semaines entre la validation des plans/la validation des finitions cabine et la sortie d'usine"
                    : isPeriod
                    ? "Nombre de semaines entre le début du chantier et la mise à disposition"
                    : "Performance = (Coût des achats réels + Coût main d'oeuvre réelle) / (Coût des achats prévus + Coût main d'oeuvre prévue)"}
                </p>
              }
            >
              <HelpIcon
                style={{
                  marginTop: "0.2rem",
                  marginLeft: "0.4rem", // space after the title
                  fontSize: "1.3rem", // match with your design
                  cursor: "pointer", // to show it's clickable
                  verticalAlign: "top", // to align with the text
                }}
              />
            </InfoTooltip>
          </>
        ) : (
          children
        )}
        {sorted && (
          <MDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={({ typography: { size } }) => ({
              fontSize: size.lg,
            })}
          >
            <MDBox
              position="absolute"
              top={-6}
              color={sorted === "asce" ? "text" : "secondary"}
              opacity={sorted === "asce" ? 1 : 0.5}
            >
              <Icon>arrow_drop_up</Icon>
            </MDBox>
            <MDBox
              position="absolute"
              top={0}
              color={sorted === "desc" ? "text" : "secondary"}
              opacity={sorted === "desc" ? 1 : 0.5}
            >
              <Icon>arrow_drop_down</Icon>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  colspan: 1,
  info: "other",
  original: "",
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  length: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  colspan: PropTypes.number,
  /*   info: PropTypes.oneOf(["planning month", "planning week", "planning day", "other"]), */
  original: PropTypes.string,
};

export default DataTableHeadCell;
